/* eslint-disable no-open-modules-imports */

import { CommentConfigDemoFeature } from 'feature/comments/comment-config';
import { LengthLimitFeature } from 'feature/comments/length-limit';
import { ReplyNotificationsFeature } from 'feature/comments/reply-notifs';
import { UpvoteFeature } from 'feature/comments/upvote';
import { ExportCommentsFeature } from 'feature/developer/import-export';
import type { FeatureGroup } from 'feature/feature-helpers';
import { CHOOSEONE, COMPOSITE, SECTION } from 'feature/feature-helpers';
import { OpenLinksInNewTabFeature } from 'feature/general/open-links-in-new-tab';
import { SatisfactionSurveyFeature, UsabilitySurveyFeature } from 'feature/general/survey';
import {
    ModerateWithGPTFeature,
    ModerateWithJigsawFeature,
    ModerationHelpFeature,
    PreModeratorFeature,
} from 'feature/moderation/ai-premoderator';
import { HideRejectedFeature } from 'feature/moderation/hide-rejected';
import { HumanReviewsEverythingFeature } from 'feature/moderation/human-reviews-everything';
import { ReportCommentFeature } from 'feature/moderation/report-comment';
import { AIQualityRankingFeature, ShowRankingFeaturesFeature } from 'feature/ranking/ai-quality-ranking';
import { RankingSelectorFeature } from 'feature/ranking/ranking-selector';
import { MostReactedFeature, MostRecentFeature, MostUpvotedFeature, OldestFeature } from 'feature/ranking/simple-ranking';
import { TopTabFeature } from 'feature/ranking/top-tab';
import { CMSEmbeddedQuestionTeaserFeature } from 'feature/teaser/editorial-question';
import { TitledWriterFeature } from 'open-modules/cbcrc/feature/general/titled-writer';
import { DialogueFeature } from 'open-modules/srg/feature/question/dialogue';
import { DialogueEmbeddedQuestionTeaserFeature } from 'open-modules/srg/feature/teaser/dialog-teaser';
import { TranslationFeature } from 'open-modules/srg/translation/TranslationFeature';
import {
    ConversationHelperAddonFeatures,
    ConversationHelperFeature,
    ConversationHelperGuidanceFeedbackSet1Feature,
    ConversationHelperGuidanceFeedbackSet2Feature,
} from 'open-modules/zdf/feature/conversation-helper';
import {
    ConversationHelperCommentBasedTimingFeature,
    ConversationHelperCommentBasedTimingFeature1,
    ConversationHelperCommentBasedTimingFeature2,
    ConversationHelperCommentBasedTimingFeature3,
    ConversationHelperCommentBasedTimingFeature4,
    ConversationHelperManualTimingFeature,
    ConversationHelperSettingsFeatures,
    GuidanceUpdateCommentIntervalFeature0,
    GuidanceUpdateCommentIntervalFeature1,
    GuidanceUpdateCommentIntervalFeature2,
    GuidanceUpdateCommentIntervalFeature3,
    GuidanceUpdateCommentIntervalFeature4,
    GuidanceUpdateIntervalFeature,
} from 'open-modules/zdf/feature/conversation-helper-admin';
import { ChannelSelectionFeature } from 'open-modules/zdf/feature/editorial-dash/display-channel-selection';
import { JumpToCommentFeature } from 'open-modules/zdf/feature/jump-to-comment';
import { AutoApproveFeature } from 'open-modules/zdf/feature/premoderation/AutoApproveFeature';
import { BlocklistFeature } from 'open-modules/zdf/feature/premoderation/blocklist';
import { PrivateCommentFeedbackFeature } from 'open-modules/zdf/feature/private-comment-feedback';
import { PushNotificationFeaturesForThreads } from 'open-modules/zdf/feature/pushNotifications/ThreadPushNotification';
import { DeleteCommentsInModDashboardFeature } from 'open-modules/zdf/feature/question/delete-commentsin-mod-dashboard';
import { EnableModTaskTreeViewFeature } from 'open-modules/zdf/feature/question/enable-mod-task-tree-view';
import { EnableModDashboardThreadDetailsFeature } from 'open-modules/zdf/feature/question/enable-thread-mod-dashboard-view';
import { VideoVotingOverviewFeature } from 'open-modules/zdf/feature/videovoting/video-voting-overview';
import { t } from 'util/stdtypes';

import { AITagFeature } from './ai-tag';
import { CloseConversationFeature } from './close-conversation';
import { CommentSliderFeature } from './comment-slider';
import { CommunityGuidelinesFeature } from './community-guidelines';
import { ContextEntryPointsFeature } from './context-entry-points';
import { EmbeddedFeature } from './embedded';
import { MostPopularFeature } from './most-popular';
import { MultiAnswerFeature } from './multi-answer';
import { PinCommentFeature } from './pin-comment';
import { PinReplyFeature } from './pin-reply';
import { PollQuestionFeature } from './poll-question';
import {
    FunReactionsFeature,
    NewPublicReactionsFeature,
    PersonalReactionsFeature,
    ReactionTypesFeature,
    ReactionTypesMultipleButtonsFeature,
    ReactionTypesPopupButtonFeature,
    SeriousReactionsFeature,
} from './reaction-types';
import { RecordEntryUrlFeature } from './record-entry-url';
import { RelatedContentFeature } from './related-content';
import { QuestionRelatedConversationsFeature } from './related-conversations';
import { RepresentingPerspectivesFeature } from './representing-perspectives';
import { SingleResponseHelpFeature } from './single-response-help';
import { StandardTemplates } from './standard-templates';

export const questionFeatures: FeatureGroup[] = [
    SECTION(t('Conversation Type'), [
        CommentSliderFeature,
        RepresentingPerspectivesFeature,
        DialogueFeature,
        COMPOSITE(ConversationHelperFeature, [
            ...ConversationHelperAddonFeatures,
            CHOOSEONE(t('Feedback Set'), [ConversationHelperGuidanceFeedbackSet1Feature, ConversationHelperGuidanceFeedbackSet2Feature]),
            PrivateCommentFeedbackFeature,
            SECTION(t('Timing of Analysis'), [
                COMPOSITE(ConversationHelperCommentBasedTimingFeature, [
                    CHOOSEONE(t('Analyze after every...'), [
                        ConversationHelperCommentBasedTimingFeature1,
                        ConversationHelperCommentBasedTimingFeature2,
                        ConversationHelperCommentBasedTimingFeature3,
                        ConversationHelperCommentBasedTimingFeature4,
                    ]),
                ]),
                ConversationHelperManualTimingFeature,
                COMPOSITE(GuidanceUpdateIntervalFeature, [
                    CHOOSEONE(t('Update based on comments...'), [
                        GuidanceUpdateCommentIntervalFeature0,
                        GuidanceUpdateCommentIntervalFeature1,
                        GuidanceUpdateCommentIntervalFeature2,
                        GuidanceUpdateCommentIntervalFeature3,
                        GuidanceUpdateCommentIntervalFeature4,
                    ]),
                ]),
            ]),
            ...ConversationHelperSettingsFeatures,
        ]),
    ]),
    SECTION(t('Moderation'), [
        HideRejectedFeature,
        COMPOSITE(PreModeratorFeature, [ModerateWithGPTFeature, ModerateWithJigsawFeature, BlocklistFeature]),
        AutoApproveFeature,
        ModerationHelpFeature,
        ReportCommentFeature,
        TopTabFeature,
        EnableModDashboardThreadDetailsFeature,
        EnableModTaskTreeViewFeature,
        DeleteCommentsInModDashboardFeature,
    ]),
    SECTION(t('General'), [
        AITagFeature,
        JumpToCommentFeature,
        LengthLimitFeature,
        CommunityGuidelinesFeature,
        ContextEntryPointsFeature,
        EmbeddedFeature,
        MultiAnswerFeature,
        MostPopularFeature,
        OpenLinksInNewTabFeature,
        PinCommentFeature,
        PinReplyFeature,
        PollQuestionFeature,
        COMPOSITE(ReactionTypesFeature, [
            CHOOSEONE(t('Interaction'), [ReactionTypesMultipleButtonsFeature, ReactionTypesPopupButtonFeature]),
            CHOOSEONE(t('Reaction Set'), [
                NewPublicReactionsFeature,
                SeriousReactionsFeature,
                PersonalReactionsFeature,
                FunReactionsFeature,
            ]),
        ]),
        RecordEntryUrlFeature,
        VideoVotingOverviewFeature,
        QuestionRelatedConversationsFeature,
        RelatedContentFeature,
        ReplyNotificationsFeature,
        CommentConfigDemoFeature,
        SingleResponseHelpFeature,
        COMPOSITE(CMSEmbeddedQuestionTeaserFeature, [DialogueEmbeddedQuestionTeaserFeature]),
        SatisfactionSurveyFeature,
        StandardTemplates,
        UsabilitySurveyFeature,
        TitledWriterFeature,
        TranslationFeature,
        UpvoteFeature,
        ExportCommentsFeature,
    ]),
    SECTION(t('Fallbacks'), [HumanReviewsEverythingFeature, CloseConversationFeature]),
    SECTION(t('Ranking'), [
        RankingSelectorFeature,
        MostRecentFeature,
        OldestFeature,
        MostUpvotedFeature,
        MostReactedFeature,
        AIQualityRankingFeature,
        ShowRankingFeaturesFeature,
    ]),
    PushNotificationFeaturesForThreads,
    SECTION(t('ZDF'), [ChannelSelectionFeature]),
];

export const questionDefaultConfig = {
    lengthlimit: false,
    upvote: false,
    reactiontypes: true,
    replynotifications: true,
    commentslider: false,
    translationfeature: false,
    community_guidelines: true,
    votingoverview: false,
    record_entry_url: true,
    aitag: true,
    pin_comment: true,
    single_response_help: true,
    satisfaction_survey: true,
    usability_survey: true,
    standard_templates: true,
    jumptocomment: true,
    context_entry_points: true,
    questionrelatedconversations: true,
    'related-content': true,

    // Radio button first features: TODO - auto-select these
    commentslider_yes: true,
    commentslider_slider: true,
    newpublicreactions: true,
    reactiontypesmultiplebuttons: true,

    titledWriter: true,

    // Moderation
    hide_rejected: true,
    aimoderation: true,
    jigsawmoderation: true,
    premoderation: true,
    report_comment: true,
    // moderationhelp: true,
    autoapprovecomments: true,
    deletecommentsinmoddashboard: true,

    //Conversation helper Settings
    conversationhelper_timing_comments: true,
    conversationhelper_timing_comments_1: true,
    conversationhelper_personality_b: true,
    conversationhelper_scope_all_comments: true,
    conversationhelper_scope_threshold_1: true,
    conversationhelper_top_x_percent_3: true,
    conversationhelper_guidance_update_comment_interval_0: true,
    conversationhelper_guidance_feedback_set_1: true,
    conversationhelper_color_purple: true,
    cmsembeddedquestionteaser: true,
    conversationhelper_ai_generated: true,
};
